import type { Player } from "../../game";
import { useId, useState } from "react";

import type { ShareResult } from "../history";
import type { MatchResult as MatchOutcome } from "../match/persistence";
import type { MatchMode } from "../match/matchMode";

import "./MatchResult.css";

export type MatchResultOutcome = MatchOutcome;

export type MatchResultProps = Readonly<{
  outcome: MatchResultOutcome;
  humanPlayer: Player;
  mode?: MatchMode;
  onNewGame: () => void;
  onReviewBoard: () => void;
  onOpenHistory: () => void;
  onShare: () => Promise<ShareResult>;
}>;

type ResultCopy = Readonly<{
  title: string;
  explanation: string;
  tone: "win" | "loss" | "draw";
}>;

export function MatchResult({
  outcome,
  humanPlayer,
  mode = "bot",
  onNewGame,
  onReviewBoard,
  onOpenHistory,
  onShare,
}: MatchResultProps) {
  const copy = resultCopy(outcome, humanPlayer, mode);
  const titleId = useId();
  const [shareResult, setShareResult] = useState<ShareResult | null>(null);
  const [sharing, setSharing] = useState(false);

  const share = async (): Promise<void> => {
    setShareResult(null);
    setSharing(true);
    try {
      setShareResult(await onShare());
    } catch {
      setShareResult(
        Object.freeze({ kind: "error", message: "The link could not be shared. Try again." }),
      );
    } finally {
      setSharing(false);
    }
  };

  return (
    <section className={`match-result match-result--${copy.tone}`} aria-labelledby={titleId}>
      <h2 id={titleId}>{copy.title}</h2>
      <p className="match-result__explanation">{copy.explanation}</p>
      <div className="match-result__actions">
        <button className="match-result__primary" type="button" onClick={onNewGame}>
          New game
        </button>
        <button className="match-result__secondary" type="button" onClick={onReviewBoard}>
          Review game
        </button>
        <button
          className="match-result__secondary"
          disabled={sharing}
          type="button"
          onClick={() => void share()}
        >
          Share game
        </button>
        <button
          className="match-result__secondary"
          data-history-action
          type="button"
          onClick={onOpenHistory}
        >
          Match history
        </button>
      </div>
      <div
        className="match-result__share-result"
        data-visible={shareResult !== null && shareResult.kind !== "cancelled"}
      >
        <p role="status" aria-atomic="true">
          {shareResult === null || shareResult.kind === "cancelled"
            ? ""
            : shareResult.kind === "copied"
              ? "Link copied."
              : shareResult.kind === "shared"
                ? "Share sheet opened."
                : (shareResult.message ?? "The link could not be shared.")}
        </p>
        {shareResult?.url === undefined ? null : (
          <label>
            Share link
            <input
              aria-label="Share link"
              readOnly
              value={shareResult.url}
              onFocus={(event) => event.currentTarget.select()}
            />
          </label>
        )}
      </div>
    </section>
  );
}

function resultCopy(outcome: MatchResultOutcome, humanPlayer: Player, mode: MatchMode): ResultCopy {
  if (outcome.kind === "manual-draw" || outcome.kind === "repetition-draw") {
    return {
      title: "Draw",
      explanation:
        outcome.kind === "repetition-draw"
          ? "The same position occurred three times."
          : "Game ended by agreement.",
      tone: "draw",
    };
  }

  if (mode === "local") {
    return {
      title: `${outcome.winner === "black" ? "Black" : "Red"} wins`,
      explanation: outcome.kind === "passage" ? "Passage" : "No legal moves",
      tone: "win",
    };
  }

  const humanWon = outcome.winner === humanPlayer;
  if (outcome.kind === "passage") {
    return humanWon
      ? {
          title: "You win",
          explanation: "Passage",
          tone: "win",
        }
      : {
          title: "Bot wins",
          explanation: "Passage",
          tone: "loss",
        };
  }

  return humanWon
    ? {
        title: "You win",
        explanation: "The bot has no legal moves",
        tone: "win",
      }
    : {
        title: "Bot wins",
        explanation: "No legal moves",
        tone: "loss",
      };
}
